import { useMutation, useQueryClient } from 'react-query'
import { notification } from 'antd'

import { QUERY_KEYS } from '../../api'
import { ContractsApi } from '../useContracts/api'

interface UpdateOrderError {
  message: string
  status: number
  type: string
}

const useUpdateOrder = () => {
  const queryClient = useQueryClient()
  const {
    mutate: updateOrder,
    isLoading: updateOrderIsLoading,
    error: updateOrderError,
  } = useMutation({
    mutationFn: ContractsApi.updateOrder,
    onSuccess: async () => {
      await queryClient.refetchQueries([QUERY_KEYS.ORDERS])
    },
    onError: (error: any) => {
      // Manejar el error específico del backend y mostrar notificación personalizada
      if (error?.response?.data) {
        const { type, status, message } = error.response.data

        // Detectar el error específico y mostrar mensaje personalizado
        if (type === 'GenericError' && status === 500) {
          notification.error({
            message: 'Pedido existente',
            description: 'El número de pedido ya existe en el sistema',
            duration: 5,
          })
        } else {
          // Para otros tipos de error, mostrar mensaje genérico
          notification.error({
            message: 'Error al actualizar el número de pedido',
            description: message || 'Error updating order number',
            duration: 5,
          })
        }
      } else {
        // Para errores de red u otros tipos
        notification.error({
          message: 'Error al actualizar el número de pedido',
          description: error?.message || 'Pedido existente',
          duration: 5,
        })
      }

      console.error('Error updating order:', error)
    },
  })

  // Función helper para extraer información del error
  const getErrorInfo = (): UpdateOrderError | null => {
    if (!updateOrderError) return null

    // Intentar extraer información del error de la respuesta del backend
    if (updateOrderError?.response?.data) {
      const { message, status, type } = updateOrderError.response.data
      return {
        message: message || 'Error updating order number',
        status: status || updateOrderError.response.status || 500,
        type: type || 'unknown',
      }
    }

    // Fallback para errores de red o otros tipos
    return {
      message: updateOrderError.message || 'Network error occurred',
      status: updateOrderError.response?.status || 500,
      type: 'network',
    }
  }

  return {
    updateOrder,
    updateOrderIsLoading,
    updateOrderError,
    getErrorInfo,
  }
}

export default useUpdateOrder
